<div class="kyc-page py-6 px-4 flex items-center justify-center">
  <div class="kyc-card max-w-md w-full">

    <!-- Header -->
    <div class="kyc-head">
      <button (click)="goBack()" class="kyc-icon-btn" aria-label="Back">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>

      <div class="text-center">
        <h1 class="kyc-title">Change Password</h1>
        <p class="kyc-subtitle">Keep your account safe with a strong password</p>
      </div>
    </div>

    <form [formGroup]="changePasswordForm" (ngSubmit)="onSubmit()" autocomplete="off" class="kyc-body">

      <!-- Old Password -->
      <div class="kyc-field mb-5">
        <label class="kyc-label">Old Password</label>
        <div class="relative">
          <input [type]="passwordVisibility['oldPassword'] ? 'text' : 'password'" id="oldPassword"
            formControlName="oldPassword" placeholder="Enter old password"
            [class.kyc-error]="changePasswordForm.get('oldPassword')?.invalid && changePasswordForm.get('oldPassword')?.touched"
            class="kyc-input pr-10" />
          <i class="fa cursor-pointer kyc-eye" [ngClass]="passwordVisibility['oldPassword'] ? 'fa-eye' : 'fa-eye-slash'"
            (click)="toggleVisibility('oldPassword')"></i>
        </div>
        <span *ngIf="changePasswordForm.get('oldPassword')?.invalid && changePasswordForm.get('oldPassword')?.touched"
          class="kyc-error-text">
          Old password is required.
        </span>
      </div>

      <!-- New Password -->
      <div class="kyc-field mb-5">
        <label class="kyc-label">New Password</label>
        <div class="relative">
          <input [type]="passwordVisibility['newPassword'] ? 'text' : 'password'" id="newPassword"
            formControlName="newPassword" placeholder="Enter new password"
            [class.kyc-error]="changePasswordForm.get('newPassword')?.invalid && changePasswordForm.get('newPassword')?.touched"
            class="kyc-input pr-10" />
          <i class="fa cursor-pointer kyc-eye" [ngClass]="passwordVisibility['newPassword'] ? 'fa-eye' : 'fa-eye-slash'"
            (click)="toggleVisibility('newPassword')"></i>
        </div>
        <span *ngIf="changePasswordForm.get('newPassword')?.invalid && changePasswordForm.get('newPassword')?.touched"
          class="kyc-error-text">
          New password is required.
        </span>

        <!-- Password Requirements -->
        <div class="kyc-guidelines"
          *ngIf="changePasswordForm.get('password')?.touched && !allRequirementsMet()">
          <div class="kyc-guideline-row">
            <input type="checkbox" [checked]="validateGuidelines('length')" disabled class="kyc-guideline-check" />
            <span [class.kyc-guideline-ok]="validateGuidelines('length')">
              Password length should be greater than 8.
            </span>
          </div>
          <div class="kyc-guideline-row">
            <input type="checkbox" [checked]="validateGuidelines('digit')" disabled class="kyc-guideline-check" />
            <span [class.kyc-guideline-ok]="validateGuidelines('digit')">
              Minimum 1 number in the password.
            </span>
          </div>
          <div class="kyc-guideline-row">
            <input type="checkbox" [checked]="validateGuidelines('uppercase')" disabled class="kyc-guideline-check" />
            <span [class.kyc-guideline-ok]="validateGuidelines('uppercase')">
              Minimum 1 capital letter in the password.
            </span>
          </div>
          <div class="kyc-guideline-row">
            <input type="checkbox" [checked]="validateGuidelines('specialChar')" disabled
              class="kyc-guideline-check" />
            <span [class.kyc-guideline-ok]="validateGuidelines('specialChar')">
              Minimum 1 special character like &#64;, #, $.
            </span>
          </div>
          <div class="kyc-guideline-row">
            <input type="checkbox" [checked]="validateGuidelines('match')" disabled class="kyc-guideline-check" />
            <span [class.kyc-guideline-ok]="validateGuidelines('match')">
              Password should match with confirm password.
            </span>
          </div>
        </div>
      </div>

      <!-- Password Strength -->
      <div class="mb-5" *ngIf="changePasswordForm.get('password')?.touched && allRequirementsMet()">
        <div class="flex justify-between mb-2">
          <span class="kyc-strength-label" [class.kyc-strength-weak]="passwordStrength < 40"
            [class.kyc-strength-medium]="passwordStrength >= 40 && passwordStrength < 70"
            [class.kyc-strength-strong]="passwordStrength >= 70">
            Password Strength: {{ getPasswordStrengthText() }}
          </span>
        </div>
        <div class="kyc-strength-track">
          <div class="kyc-strength-fill" [style.width.%]="passwordStrength" [class.kyc-strength-weak-bg]="passwordStrength < 40"
            [class.kyc-strength-medium-bg]="passwordStrength >= 40 && passwordStrength < 70"
            [class.kyc-strength-strong-bg]="passwordStrength >= 70"></div>
        </div>
      </div>

      <!-- Confirm Password -->
      <div class="kyc-field mb-6">
        <label class="kyc-label">Confirm Password</label>
        <div class="relative">
          <input [type]="passwordVisibility['confirmPassword'] ? 'text' : 'password'" id="confirmPassword"
            formControlName="confirmPassword" placeholder="Confirm new password"
            [class.kyc-error]="changePasswordForm.get('confirmPassword')?.invalid && changePasswordForm.get('confirmPassword')?.touched"
            class="kyc-input pr-10" />
          <i class="fa cursor-pointer kyc-eye"
            [ngClass]="passwordVisibility['confirmPassword'] ? 'fa-eye' : 'fa-eye-slash'"
            (click)="toggleVisibility('confirmPassword')"></i>
        </div>
        <span
          *ngIf="changePasswordForm.get('confirmPassword')?.invalid && changePasswordForm.get('confirmPassword')?.touched"
          class="kyc-error-text">
          Confirm password is required.
        </span>
        <span
          *ngIf="changePasswordForm.get('confirmPassword')?.value !== changePasswordForm.get('newPassword')?.value && changePasswordForm.get('confirmPassword')?.touched"
          class="kyc-error-text">
          Passwords do not match.
        </span>
      </div>

      <!-- Submit -->
      <div class="flex gap-3 justify-end">
        <button type="button" (click)="goBack()" class="kyc-btn-ghost">
          Cancel
        </button>
        <button type="submit" [disabled]="changePasswordForm.invalid" class="kyc-btn-gold flex-1">
          Reset Password
        </button>
      </div>
    </form>
  </div>
</div>
